import { FC, useEffect, useRef } from 'react';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useNotificationsListApi } from '@/features/notifications/hooks/useNotificationsListApi';
import { Roles } from '@/@types/auth.types';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { NotificationsUtils } from '@/features/notifications/utils/notificationsUtils';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { t } from 'i18next';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import NotificationsListEmpty from '@/features/notifications/componentns/NotificationsList/NotificationsListEmpty';

type Props = {
	showDetails?: (id: string) => void;
};

const NotificationsOwnerList: FC<Props> = ({ showDetails }) => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const loaderRef = useRef<HTMLDivElement | null>(null);

	const {
		ownerNotificationsListData,
		isLoadingOwnerNotificationsListData,
		fetchOwnerNotificationsListDataNextPage,
		hasOwnerNotificationsListDataNextPage,
		isFetchingOwnerNotificationsListDataNextPage,
	} = useNotificationsListApi({
		role: Roles.owner,
	});

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOwnerNotificationsListDataNextPage ||
			isFetchingOwnerNotificationsListDataNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOwnerNotificationsListDataNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '500px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOwnerNotificationsListDataNextPage,
		hasOwnerNotificationsListDataNextPage,
		isFetchingOwnerNotificationsListDataNextPage,
	]);

	if (!ownerNotificationsListData) return null;

	const data = Object.entries(ownerNotificationsListData);

	return (
		<div
			className={cn(
				'p-6 flex-1 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
				isMobile && 'p-0 border-0 bg-popover-30 gap-4',
			)}>
			<div className='flex-1'>
				{isLoadingOwnerNotificationsListData && (
					<EActivitiesListSkeleton isMobile={isMobile} />
				)}
				{!isLoadingOwnerNotificationsListData && !!data.length && (
					<div
						className={cn(
							'flex flex-col gap-6',
							isMobile && 'gap-4',
						)}>
						{data.map(([date, notifications]) => {
							return (
								<div key={`${date}`}>
									<h5 className='text-sm text-popover-800'>
										{date}
									</h5>
									{notifications?.map((notification) => {
										const { label, icon, iconColors } =
											NotificationsUtils.getNotificationsConfig(
												notification.category,
											);

										const header = `${label} ${
											notification.isRequired
												? ''
												: '(optional)'
										}`;

										return (
											<EActivitiesElement
												key={notification.id}
												icon={icon}
												colorClass={iconColors}
												header={header}
												description={
													notification.message ??
													t(
														'main.notifications.components.list.noTitle',
													)
												}
												value={DateTimeHelper.formatTime(
													notification.sent,
												)}
												onClick={showDetails}
												id={notification.id}
												isMobile={isMobile}
											/>
										);
									})}
								</div>
							);
						})}
						<div
							ref={loaderRef}
							className='h-px'
						/>
					</div>
				)}
				{!isLoadingOwnerNotificationsListData && !data.length && (
					<NotificationsListEmpty />
				)}
			</div>
		</div>
	);
};

export default NotificationsOwnerList;
